import { useState, useCallback } from "react";
import dayjs from "dayjs";
import { DEFAULT_PRIVATE_ASSIGNEES } from "../../../constant/constant";

// Detect user's local timezone from the browser — falls back to UTC if unavailable
const USER_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

const INITIAL = {
  // Step 1 — task details
  title:          "",
  description:    "",
  priority:       1,
  assignees:      [],     // [{ _id, label, file }] — objects, so StepReview can show labels
  support:        null,   // { _id, name, label }
  acknowledgedBy: null,   // { _id, name, label }
  defaultStatus:  1,      // 1=To Do  2=In Progress  3=Completed
  privateAssignees: DEFAULT_PRIVATE_ASSIGNEES,

  // Step 2 — recurrence
  recurrenceType:       "weekly",
  recurrenceInterval:   1,
  recurrenceDaysOfWeek: [],
  recurrenceDayOfMonth: 1,
  recurrenceNthWeekday: { n: 1, d: 1 },
  monthlyMode:          "fixed",   // "fixed" | "nth" — persisted; the server branches on it
  recurrenceMonth:      0,
  recurringStartDate:   dayjs().format("YYYY-MM-DD"),
  recurrenceEndType:    "never",
  recurrenceEndDate:    null,
  recurrenceEndAfter:   null,
  recurrenceTimezone:   USER_TIMEZONE,
  generationWindow:     14,
};

const useRecurringForm = (initialValues = null) => {
  const base = initialValues || INITIAL;
  const [step,       setStep]       = useState(0);   // 0=Details 1=Recurrence 2=Review
  const [values,     setValues]     = useState(base);
  const [errors,     setErrors]     = useState({});
  const [submitting, setSubmitting] = useState(false);

  const set = useCallback((field, value) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => { const e = { ...prev }; delete e[field]; return e; });
  }, []);

  const setMany = useCallback((patch) => {
    setValues((prev) => ({ ...prev, ...patch }));
  }, []);

  const validateStep = useCallback((s) => {
    const errs = {};
    if (s === 0) {
      if (!values.title.trim()) errs.title    = "Title is required";
      if (!values.priority)     errs.priority = "Priority is required";
    }
    if (s === 1) {
      if (!values.recurrenceType)
        errs.recurrenceType = "Select a recurrence type";
      if (values.recurrenceType === "weekly" && !values.recurrenceDaysOfWeek.length)
        errs.recurrenceDaysOfWeek = "Select at least one day";
      if (!values.recurringStartDate)
        errs.recurringStartDate = "Start date is required";
      if (values.recurrenceEndType === "on" && !values.recurrenceEndDate)
        errs.recurrenceEndDate = "End date is required";
      if (values.recurrenceEndType === "after" && !values.recurrenceEndAfter)
        errs.recurrenceEndAfter = "Number of occurrences is required";
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }, [values]);

  const nextStep = useCallback(() => {
    if (validateStep(step)) setStep((s) => Math.min(s + 1, 2));
  }, [step, validateStep]);

  const prevStep = useCallback(() => setStep((s) => Math.max(s - 1, 0)), []);

  const reset = useCallback(() => {
    setValues(base);
    setErrors({});
    setStep(0);
  }, [base]);

  /** Builds the clean payload to POST to the API */
  const buildPayload = useCallback(() => {
    const payload = {
      title:              values.title.trim(),
      description:        values.description.trim(),
      priority:           values.priority,
      defaultStatus:      values.defaultStatus,
      // Sent UNCONDITIONALLY: if this were gated on truthiness, turning
      // privacy OFF on an existing template would be inexpressible and every
      // future occurrence would stay private.
      privateAssignees:   !!values.privateAssignees,
      // Send only `assignees`; the backend owns the `assignee` scalar mirror.
      // Sending both risks the two disagreeing.
      assignees:          (values.assignees || []).map((a) => String(a?._id || a)).filter(Boolean),
      support:            values.support?._id        || values.support        || null,
      acknowledgedBy:     values.acknowledgedBy?._id || values.acknowledgedBy || null,
      recurrenceType:     values.recurrenceType,
      recurrenceInterval: values.recurrenceInterval,
      recurringStartDate: values.recurringStartDate,
      recurrenceEndType:  values.recurrenceEndType,
      recurrenceTimezone: values.recurrenceTimezone,
      generationWindow:   values.generationWindow,
    };

    if (values.recurrenceType === "weekly")
      payload.recurrenceDaysOfWeek = values.recurrenceDaysOfWeek;

    if (values.recurrenceType === "monthly") {
      // The mode has to be persisted: the server cannot tell "day 5" from
      // "first Monday" by field presence alone, because only one side is sent
      // and the other lingers from a previous save.
      payload.monthlyMode = values.monthlyMode;
      if (values.monthlyMode === "nth")
        payload.recurrenceNthWeekday = values.recurrenceNthWeekday;
      else
        payload.recurrenceDayOfMonth = values.recurrenceDayOfMonth;
    }

    if (values.recurrenceType === "yearly") {
      payload.recurrenceMonth      = values.recurrenceMonth;
      payload.recurrenceDayOfMonth = values.recurrenceDayOfMonth;
    }

    if (values.recurrenceEndType === "on")
      payload.recurrenceEndDate  = values.recurrenceEndDate;
    if (values.recurrenceEndType === "after")
      payload.recurrenceEndAfter = values.recurrenceEndAfter;

    return payload;
  }, [values]);

  return {
    step, values, errors, submitting,
    set, setMany, setSubmitting,
    nextStep, prevStep, reset,
    buildPayload,
    isFirstStep: step === 0,
    isLastStep:  step === 2,
  };
};

export default useRecurringForm;
